/**
 * Client-side plan-quota pre-check — the "don't start what can't finish"
 * half of the limit UX. The SERVER stays the authority (quota.service's
 * atomic reserve at job creation); this only decides whether to send the
 * request at all, using the same numbers the server uses:
 *
 *   remaining = max(0, monthly limit − used) + referral bonus credits
 *
 * as reported by GET /api/billing/usage (`features[].remaining`, cents
 * for the changes budget, `changeCosts` for what each change draws).
 *
 *   ensureQuota('imageAds', 10)      → true: go ahead (maybe with a
 *                                      "3 image ads left · View plans"
 *                                      toast when ≤ 20% would remain)
 *                                    → false: the plan overlay opened,
 *                                      the job was NOT started
 *   ensureChangesBudget('websiteBuild') → the same for cents-priced actions
 *
 * Fail-open by design: if usage can't be read (offline, 500, a backend
 * without `remaining`), the action proceeds and the server answers. A
 * server quota error (403 + upgrade + cap code) is caught centrally in
 * lib/api.js → openPlanLimit(), so nothing slips past either way.
 */
import { apiFetch } from './api';
import { PLAN_LIMIT_EVENT, QUOTA_TOAST_EVENT, USAGE_CHANGED_EVENT, QUOTA_CAP_CODES, emitQuotaEvent } from './quotaEvents';

export { PLAN_LIMIT_EVENT, QUOTA_TOAST_EVENT, USAGE_CHANGED_EVENT, QUOTA_CAP_CODES };
export const NEAR_LIMIT_RATIO = 0.2;
const CACHE_MS = 20000;
const USAGE_TIMEOUT_MS = 8000;

// Fallback cents per change (plan-quotas.config CHANGE_COSTS) — only used
// when the backend's usage payload predates `changeCosts`.
const DEFAULT_CHANGE_COSTS = { refine: 45, extraProductImage: 10, websiteBuild: 140, cartUpgrade: 15, chatMessage: 8 };

let cache = { at: 0, data: null, promise: null };
// What the last pre-check said an action needs, per feature — the
// overlay uses it when the server (not the pre-check) says no, so the
// "not enough" copy can still name the amount.
const lastNeeded = {};
// Near-limit toasts repeat at most once a minute per feature (a chat
// turn or a per-image restyle would otherwise toast on every click).
const NEAR_TOAST_EVERY_MS = 60000;
const lastNearToastAt = {};

export function invalidateUsage() {
  cache = { at: 0, data: null, promise: null };
}

// A job just started / a 403 came back / the plan changed (lib/api.js
// emits this without importing us): drop the cached numbers so the next
// pre-check reads the post-reserve usage instead of a stale copy.
try { window.addEventListener(USAGE_CHANGED_EVENT, invalidateUsage); } catch { /* SSR/test safety */ }

/** Cached (20 s) usage payload, or null when it can't be read. */
export async function fetchUsage({ force = false } = {}) {
  const now = Date.now();
  if (!force && cache.data && now - cache.at < CACHE_MS) return cache.data;
  if (!force && cache.promise) return cache.promise;
  const promise = apiFetch('/api/billing/usage', { credentials: 'include', timeoutMs: USAGE_TIMEOUT_MS })
    .then((r) => (r.ok ? r.json() : null))
    .then((d) => {
      if (d && d.success) { cache = { at: Date.now(), data: d, promise: null }; return d; }
      cache.promise = null;
      return null;
    })
    .catch(() => { cache.promise = null; return null; });
  cache.promise = promise;
  return promise;
}

/** Cents a changes-budget action draws (server config, else the known defaults). */
export function changeCost(usage, kind, count = 1) {
  const costs = usage?.changeCosts || DEFAULT_CHANGE_COSTS;
  const unit = Number(costs[kind]) || DEFAULT_CHANGE_COSTS[kind] || 0;
  return unit * Math.max(1, Math.floor(Number(count) || 1));
}

export function featureRow(usage, feature) {
  return (usage?.features || []).find((f) => f.key === feature) || null;
}

/** Limit in the feature's base units (cents for the dollar pool), or Infinity. */
export function limitUnits(row) {
  if (!row || row.limit === 'unlimited' || row.limit === Infinity) return Infinity;
  const n = Number(row.limit);
  if (!Number.isFinite(n)) return Infinity;
  return row.unit === 'dollars' ? Math.round(n * 100) : n;
}

/** Remaining (base + bonus) in base units, Infinity when unlimited, null when unknown. */
export function remainingUnits(row) {
  if (!row) return null;
  if (row.remaining === 'unlimited' || row.remaining === Infinity) return Infinity;
  if (row.remaining === undefined || row.remaining === null) {
    // Older backend without `remaining`: derive base − used (no bonus known).
    const limit = limitUnits(row);
    if (limit === Infinity) return Infinity;
    const used = row.unit === 'dollars' ? Math.round((Number(row.used) || 0) * 100) : Number(row.used) || 0;
    return Math.max(0, limit - used);
  }
  const n = Number(row.remaining);
  return Number.isFinite(n) ? Math.max(0, n) : null;
}

/** "product builds" / "changes allowance" — the server label, sentence case. */
export function featureLabel(row, feature) {
  const label = row?.label || String(feature || 'credits').replace(/([A-Z])/g, ' $1');
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/** "$1.40" for the dollar pool, "10" otherwise. */
export function formatUnits(row, units) {
  if (units === Infinity) return '∞';
  if (row?.unit === 'dollars') return `$${(Math.max(0, units) / 100).toFixed(2)}`;
  return String(Math.max(0, Math.round(units)));
}

export function rememberNeeded(feature, needed) {
  if (feature && Number.isFinite(needed) && needed > 0) lastNeeded[feature] = needed;
}
export function lastNeededFor(feature) {
  return lastNeeded[feature] || null;
}

/** Open the plan overlay (the central 403 handler and the pre-checks both land here). */
export function openPlanLimit(detail = {}) {
  emitQuotaEvent(PLAN_LIMIT_EVENT, detail);
}

/** Small non-blocking toast (near-limit, "plan upgraded"). */
export function showQuotaToast(message, extra = {}) {
  emitQuotaEvent(QUOTA_TOAST_EVENT, { message, ...extra });
}

export function notifyUsageChanged() {
  invalidateUsage();
  emitQuotaEvent(USAGE_CHANGED_EVENT, {});
}

/**
 * Decide whether an action that needs `needed` units of `feature` may
 * start. Returns a promise of true (go) / false (overlay opened).
 */
export async function ensureQuota(feature, needed = 1) {
  const need = Math.max(1, Math.floor(Number(needed) || 1));
  rememberNeeded(feature, need);
  const usage = await fetchUsage();
  const row = featureRow(usage, feature);
  if (!row) return true; // unknown feature / unreadable usage → the server decides
  const remaining = remainingUnits(row);
  if (remaining === null || remaining === Infinity) return true;
  const limit = limitUnits(row);
  if (remaining < need) {
    openPlanLimit({
      feature,
      code: feature === 'changesBudget' ? 'changes_budget_exhausted' : 'quota_exceeded',
      needed: need,
      remaining,
      source: 'precheck',
    });
    return false;
  }
  const leftAfter = remaining - need;
  if (limit !== Infinity && leftAfter <= limit * NEAR_LIMIT_RATIO) {
    const now = Date.now();
    if (now - (lastNearToastAt[feature] || 0) >= NEAR_TOAST_EVERY_MS) {
      lastNearToastAt[feature] = now;
      showQuotaToast(nearLimitMessage(row, feature, leftAfter), { feature, action: 'plans' });
    }
  }
  return true;
}

/** Test hook: forget the near-limit toast timestamps. */
export function resetNearLimitToasts() {
  for (const k of Object.keys(lastNearToastAt)) delete lastNearToastAt[k];
}

/** Pre-check for a cents-priced change: `kind` is a CHANGE_COSTS key. */
export async function ensureChangesBudget(kind, count = 1) {
  const usage = await fetchUsage();
  return ensureQuota('changesBudget', changeCost(usage, kind, count));
}

/** "1 product build" vs "3 product builds" — the server labels are plural. */
export function unitsLabel(row, feature, units) {
  const label = featureLabel(row, feature);
  if (row?.unit === 'dollars' || Math.round(units) !== 1) return label;
  return label.replace(/ches$/, 'ch').replace(/([^s])s$/, '$1');
}

export function nearLimitMessage(row, feature, left) {
  const label = unitsLabel(row, feature, left);
  return row?.unit === 'dollars'
    ? `${formatUnits(row, left)} of ${label} left this month`
    : `${formatUnits(row, left)} ${label} left this month`;
}

/** Is this HTTP response a monthly quota CAP (as opposed to a plan gate)? */
export function isQuotaCapResponse(res, data) {
  return !!(res && res.status === 403 && data && data.upgrade === true && QUOTA_CAP_CODES.includes(data.code));
}
